import { createSlice } from "@reduxjs/toolkit";

import {
    normalizeCacheId,
    readDetailCache,
    writeDetailCache,
} from "../../../helpers/redux/detailCache";
import {
    getAllLeadConnectionAsync,
    getSingleConnectionRedux,
    refreshSource,
} from "./leadConnection.asyncActions";

const initialState = {
    getAllLeadConnection: {
        message: "",
        status: "idle",
        leadConnection: [],
        singleConnection: [],
        singleConnectionStatus: "idle",
        singleConnectionMessage: "",
        singleConnectionErrorStatus: null,
        // Id of the connection the detail page is currently interested in.
        // Responses for any other id are dropped (stale-response guard).
        activeConnectionId: null,
        // { [id]: { data, fetchedAt } } - used to hydrate singleConnection
        // instantly on page enter while a fresh request is in flight.
        connectionCacheById: {},
        isRefreshSourceLoading: false,
        shouldRefetchSourcePages: false,
    },
};

const isActiveConnection = (state, id) =>
    normalizeCacheId(id) === state.getAllLeadConnection.activeConnectionId;

/**
 * Makes `id` the active connection and syncs the current slot with it:
 * cached data if we have it, otherwise an empty slot so the page never shows
 * a previously viewed connection while the new one is loading.
 */
const activateConnection = (state, id) => {
    const normalizedId = normalizeCacheId(id);
    const slice = state.getAllLeadConnection;
    slice.activeConnectionId = normalizedId;
    const cached = readDetailCache(slice.connectionCacheById, normalizedId);
    slice.singleConnection = cached?.data ?? [];
    slice.singleConnectionMessage = "";
    slice.singleConnectionErrorStatus = null;
};

export const leadConnectionSlice = createSlice({
    name: "getAllLeadConnectionState",
    initialState,
    reducers: {
        clearState: (state) => {
            state.getAllLeadConnection.singleConnection = [];
            state.getAllLeadConnection.singleConnectionMessage = "";
            state.getAllLeadConnection.activeConnectionId = null;
        },
        clearConnectionCache: (state) => {
            state.getAllLeadConnection.connectionCacheById = {};
        },
        hydrateSingleConnectionFromCache: (state, action) => {
            activateConnection(state, action.payload);
        },
        clearRefetchSourcePages: (state) => {
            state.getAllLeadConnection.shouldRefetchSourcePages = false;
        },
        setSingleConnectionName: (state, action) => {
            const {
                singleConnection,
                activeConnectionId,
                connectionCacheById,
            } = state.getAllLeadConnection;
            if (singleConnection?.connection_detail) {
                singleConnection.connection_detail.name = action.payload;
            }
            const cached = readDetailCache(
                connectionCacheById,
                activeConnectionId,
            );
            if (cached?.data?.connection_detail) {
                cached.data.connection_detail.name = action.payload;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getAllLeadConnectionAsync.pending, (state) => {
                state.getAllLeadConnection.status = "loading";
            })
            .addCase(getAllLeadConnectionAsync.fulfilled, (state, action) => {
                state.getAllLeadConnection.leadConnection =
                    action.payload.data && action.payload.data;
                state.getAllLeadConnection.status = "success";
                state.getAllLeadConnection.message =
                    action.payload.message && action.payload.message;
            })
            .addCase(getAllLeadConnectionAsync.rejected, (state, action) => {
                state.getAllLeadConnection.status = "failed";
                state.getAllLeadConnection.message = action.payload?.message;
            })
            .addCase(getSingleConnectionRedux.pending, (state, action) => {
                // If the page didn't hydrate first (different id than the one
                // currently active), swap the slot ourselves so stale data from
                // another connection is never shown under the loader.
                if (!isActiveConnection(state, action.meta.arg)) {
                    activateConnection(state, action.meta.arg);
                }
                state.getAllLeadConnection.singleConnectionStatus = "loading";
                state.getAllLeadConnection.singleConnectionErrorStatus = null;
                state.getAllLeadConnection.singleConnectionMessage = "";
            })
            .addCase(getSingleConnectionRedux.fulfilled, (state, action) => {
                if (!isActiveConnection(state, action.meta.arg)) {
                    return;
                }
                const data = action.payload.data && action.payload.data;
                state.getAllLeadConnection.singleConnectionStatus = "success";
                state.getAllLeadConnection.singleConnection = data;
                state.getAllLeadConnection.singleConnectionMessage =
                    action.payload.message && action.payload.message;
                if (data) {
                    writeDetailCache(
                        state.getAllLeadConnection.connectionCacheById,
                        action.meta.arg,
                        data,
                    );
                }
            })
            .addCase(getSingleConnectionRedux.rejected, (state, action) => {
                if (
                    action.meta.aborted ||
                    !isActiveConnection(state, action.meta.arg)
                ) {
                    return;
                }
                state.getAllLeadConnection.singleConnectionStatus = "failed";
                state.getAllLeadConnection.singleConnectionErrorStatus =
                    action.payload?.status ?? null;
            });

        builder
            .addCase(refreshSource.pending, (state) => {
                state.getAllLeadConnection.isRefreshSourceLoading = true;
            })
            .addCase(refreshSource.fulfilled, (state) => {
                state.getAllLeadConnection.isRefreshSourceLoading = false;
                state.getAllLeadConnection.shouldRefetchSourcePages = true;
            })
            .addCase(refreshSource.rejected, (state) => {
                state.getAllLeadConnection.isRefreshSourceLoading = false;
            });
    },
});
export default leadConnectionSlice.reducer;
export const {
    clearState,
    clearConnectionCache,
    hydrateSingleConnectionFromCache,
    clearRefetchSourcePages,
    setSingleConnectionName,
} = leadConnectionSlice.actions;
